import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Line } from "react-chartjs-2";
import featureService from "../../api/services/dashbaordService";
import Loading from "../Loading";
import DashboardPaymentCards from "./DashboardPaymentCards";
import {
  formatCurrencyLine,
  filterCurrencies,
  ensureAllCurrencies,
  uniqueCurrencyCodesFromRows,
} from "./DashboardPaymentCards";

const THEME = {
  primary: "#FB565F",
  primaryLight: "#fecdd3",
  success: "#10b981",
  info: "#3b82f6",
};

/** One color per currency for line charts; unknown codes get a stable HSL from the code. */
const CURRENCY_COLORS = {
  PKR: "#FB565F",
  AED: "#10b981",
  EUR: "#3b82f6",
  GBP: "#f59e0b",
  USD: "#6366f1",
  SAR: "#14b8a6",
  INR: "#f97316",
  KWD: "#a855f7",
};

function colorForCurrency(code) {
  if (CURRENCY_COLORS[code]) return CURRENCY_COLORS[code];
  let h = 0;
  for (let i = 0; i < code.length; i += 1) h = (h * 31 + code.charCodeAt(i)) % 360;
  return `hsl(${h}, 50%, 42%)`;
}

function collectCurrencyCodesFromMonthlyRows(rows) {
  const set = new Set();
  (rows || []).forEach((row) => {
    filterCurrencies(row.by_currency || []).forEach((c) => {
      if (Number(c.count) > 0 || Number(c.amount) > 0) set.add(c.currency);
    });
  });
  return uniqueCurrencyCodesFromRows(Array.from(set, (currency) => ({ currency })));
}

function collectCurrencyCodesFromDayRows(days) {
  const set = new Set();
  (days || []).forEach((d) => {
    filterCurrencies(d.by_currency || []).forEach((c) => {
      if (Number(c.count) > 0 || Number(c.amount) > 0) set.add(c.currency);
    });
  });
  return uniqueCurrencyCodesFromRows(Array.from(set, (currency) => ({ currency })));
}

const TABS = [
  { id: "overview", label: "Overview Subscriptions" },
  { id: "monthwise", label: "Month-wise" },
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MONTH_NAMES_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 12 }, (_, i) => currentYear - 5 + i);

const defaultFilters = {
  date: "",
  month: "",
  year: "",
  currency: "",
};

function buildParams(f) {
  const params = {};
  if (f.date) params.date = f.date;
  const hasMonth = f.month !== "" && f.month != null;
  const hasYear = f.year !== "" && f.year != null;
  if (hasMonth) {
    params.month = Number(f.month);
    params.year = hasYear ? Number(f.year) : new Date().getFullYear();
  }
  if (hasYear && !hasMonth) {
    params.year = Number(f.year);
    params.month = new Date().getMonth() + 1;
  }
  if (f.currency) params.currency = f.currency;
  return params;
}

/** Line chart options with dual Y-axis: left = Count, right = Amount. */
const LINE_OPTIONS_DUAL_AXIS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { position: "top" } },
  scales: {
    x: { grid: { display: false } },
    y: {
      type: "linear",
      display: true,
      position: "left",
      beginAtZero: true,
      title: { display: true, text: "Payment count" },
      ticks: { stepSize: 1 },
    },
    yAmount: {
      type: "linear",
      display: true,
      position: "right",
      beginAtZero: true,
      title: { display: true, text: "Amount" },
      grid: { drawOnChartArea: false },
    },
  },
};

export default function DashboardPaymentSection({
  initialPaymentAnalytics,
  monthlyPaymentStats = [],
  monthlyPaymentStatsByCurrency = [],
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const [filters, setFilters] = useState(defaultFilters);
  const [paymentAnalytics, setPaymentAnalytics] = useState(initialPaymentAnalytics ?? null);
  const [paymentsByDay, setPaymentsByDay] = useState([]);
  const [loadingByDay, setLoadingByDay] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const currencyOptions = useMemo(
    () =>
      uniqueCurrencyCodesFromRows(
        initialPaymentAnalytics?.by_currency,
        paymentAnalytics?.by_currency
      ),
    [initialPaymentAnalytics, paymentAnalytics]
  );

  const hasActiveFilters =
    !!filters.date ||
    (filters.month !== "" && filters.month != null) ||
    (filters.year !== "" && filters.year != null) ||
    !!filters.currency;

  const activeFilterPills = [];
  if (filters.date) activeFilterPills.push({ key: "date", label: `Date: ${filters.date}` });
  if (filters.month !== "" && filters.month != null && filters.year !== "" && filters.year != null) {
    activeFilterPills.push({
      key: "month",
      label: `Month: ${MONTHS[Number(filters.month) - 1]} ${filters.year}`,
    });
  }
  if (filters.currency) activeFilterPills.push({ key: "currency", label: `Currency: ${filters.currency}` });

  const removeFilter = (key) => {
    if (key === "date") handleFilterChange("date", "");
    if (key === "month") {
      handleFilterChange("month", "");
      handleFilterChange("year", "");
    }
    if (key === "currency") handleFilterChange("currency", "");
  };

  const fetchWithFilters = useCallback(async (f) => {
    setLoading(true);
    setError(null);
    try {
      const params = buildParams(f);
      const result = await featureService.fetchPaymentStats(params);
      if (result?.responseCode === 200 && result?.paymentAnalytics) {
        setPaymentAnalytics(result.paymentAnalytics);
      }
    } catch (err) {
      setError(err?.message ?? "Failed to load payment stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasActiveFilters) {
      setPaymentAnalytics(initialPaymentAnalytics ?? null);
      setError(null);
      return;
    }
    fetchWithFilters(filters);
  }, [filters.date, filters.month, filters.year, filters.currency]);

  useEffect(() => {
    if (!hasActiveFilters && initialPaymentAnalytics != null) {
      setPaymentAnalytics(initialPaymentAnalytics);
    }
  }, [initialPaymentAnalytics, hasActiveFilters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClear = () => {
    setFilters(defaultFilters);
    setPaymentAnalytics(initialPaymentAnalytics ?? null);
    setError(null);
  };

  const selectedCurrency = filters.currency || null;
  const todayByCurrency = ensureAllCurrencies(paymentAnalytics?.today?.by_currency ?? []);

  const chartMonth = filters.month !== "" && filters.month != null ? Number(filters.month) : new Date().getMonth() + 1;
  const chartYear = filters.year !== "" && filters.year != null ? Number(filters.year) : new Date().getFullYear();

  useEffect(() => {
    if (activeTab !== "monthwise") return;
    const fetchByDay = async () => {
      setLoadingByDay(true);
      try {
        const result = await featureService.fetchPaymentStatsByDay(chartMonth, chartYear, selectedCurrency || undefined);
        if (result?.responseCode === 200 && result?.paymentsByDay) {
          setPaymentsByDay(result.paymentsByDay);
        } else {
          setPaymentsByDay([]);
        }
      } catch {
        setPaymentsByDay([]);
      } finally {
        setLoadingByDay(false);
      }
    };
    fetchByDay();
  }, [activeTab, chartMonth, chartYear, selectedCurrency]);

  const monthlySource = useMemo(
    () =>
      monthlyPaymentStatsByCurrency.length > 0
        ? monthlyPaymentStatsByCurrency
        : monthlyPaymentStats.map((d) => ({
            month: d.month,
            year: d.year,
            by_currency: [{ currency: "PKR", count: d.count, amount: d.amount }],
          })),
    [monthlyPaymentStatsByCurrency, monthlyPaymentStats]
  );

  const monthlyChartCurrencies = useMemo(
    () => collectCurrencyCodesFromMonthlyRows(monthlySource),
    [monthlySource]
  );

  // Day-by-day: support both by_currency (new) and flat count/amount (legacy)
  const dayLabels = paymentsByDay.map((d) => String(d.day));
  const dayHasByCurrency = paymentsByDay.length > 0 && paymentsByDay[0].by_currency != null;
  const dayChartCurrencies = useMemo(() => {
    if (!paymentsByDay.length || !dayHasByCurrency) return [];
    return collectCurrencyCodesFromDayRows(paymentsByDay);
  }, [paymentsByDay, dayHasByCurrency]);

  const dayDatasets = dayHasByCurrency
    ? (() => {
        let codes = dayChartCurrencies.length > 0 ? dayChartCurrencies : selectedCurrency ? [String(selectedCurrency).toUpperCase()] : [];
        if (codes.length === 0) {
          const set = new Set();
          paymentsByDay.forEach((d) => {
            (d.by_currency || []).forEach((x) => {
              const u = (x.currency || "").toUpperCase().trim();
              if (u) set.add(u);
            });
          });
          codes = uniqueCurrencyCodesFromRows(Array.from(set, (currency) => ({ currency })));
        }
        if (codes.length === 0) {
          return [
            { label: "Payment count", data: paymentsByDay.map((d) => (d.by_currency || []).reduce((s, x) => s + (Number(x.count) || 0), 0)), borderColor: THEME.primary, backgroundColor: "transparent", fill: false, tension: 0.3, yAxisID: "y" },
            { label: "Amount", data: paymentsByDay.map((d) => (d.by_currency || []).reduce((s, x) => s + (Number(x.amount) || 0), 0)), borderColor: THEME.info, backgroundColor: "transparent", fill: false, tension: 0.3, yAxisID: "yAmount" },
          ];
        }
        const datasets = [];
        codes.forEach((cur) => {
          const color = colorForCurrency(cur);
          const countData = paymentsByDay.map((d) => {
            const c = (d.by_currency || []).find((x) => (x.currency || "").toUpperCase() === cur);
            return c ? c.count : 0;
          });
          const amountData = paymentsByDay.map((d) => {
            const c = (d.by_currency || []).find((x) => (x.currency || "").toUpperCase() === cur);
            return c ? c.amount : 0;
          });
          datasets.push({ label: `${cur} (count)`, data: countData, borderColor: color, backgroundColor: "transparent", fill: false, tension: 0.3, yAxisID: "y" });
          datasets.push({ label: `${cur} (amount)`, data: amountData, borderColor: color, backgroundColor: "transparent", fill: false, tension: 0.3, borderDash: [4, 2], yAxisID: "yAmount" });
        });
        return datasets;
      })()
    : [
        { label: "Payment count", data: paymentsByDay.map((d) => d.count ?? 0), borderColor: THEME.primary, backgroundColor: "transparent", fill: false, tension: 0.3, yAxisID: "y" },
        { label: "Amount", data: paymentsByDay.map((d) => d.amount ?? 0), borderColor: THEME.info, backgroundColor: "transparent", fill: false, tension: 0.3, yAxisID: "yAmount" },
      ];
  const paymentsByDayChartData = {
    labels: dayLabels.length ? dayLabels : ["—"],
    datasets: dayDatasets,
  };

  const monthlyLabels = monthlySource.map((d) => `${MONTH_NAMES_SHORT[Number(d.month) - 1]} ${d.year}`);
  const monthlyDatasets = (() => {
    const hasByCurrency = monthlySource.length > 0 && monthlySource[0].by_currency != null;
    if (!hasByCurrency) {
      return [
        { label: "Payment count", data: monthlyPaymentStats.map((d) => d.count), borderColor: THEME.primary, backgroundColor: "transparent", fill: false, tension: 0.3, yAxisID: "y" },
        { label: "Amount", data: monthlyPaymentStats.map((d) => d.amount), borderColor: THEME.info, backgroundColor: "transparent", fill: false, tension: 0.3, yAxisID: "yAmount" },
      ];
    }
    const datasets = [];
    const codes = monthlyChartCurrencies;
    if (codes.length === 0) {
      return [
        { label: "Payment count", data: monthlySource.map((d) => (d.by_currency || []).reduce((s, x) => s + (Number(x.count) || 0), 0)), borderColor: THEME.primary, backgroundColor: "transparent", fill: false, tension: 0.3, yAxisID: "y" },
        { label: "Amount", data: monthlySource.map((d) => (d.by_currency || []).reduce((s, x) => s + (Number(x.amount) || 0), 0)), borderColor: THEME.info, backgroundColor: "transparent", fill: false, tension: 0.3, yAxisID: "yAmount" },
      ];
    }
    codes.forEach((cur) => {
      const color = colorForCurrency(cur);
      const countData = monthlySource.map((d) => {
        const c = (d.by_currency || []).find((x) => (x.currency || "").toUpperCase() === cur);
        return c ? c.count : 0;
      });
      const amountData = monthlySource.map((d) => {
        const c = (d.by_currency || []).find((x) => (x.currency || "").toUpperCase() === cur);
        return c ? c.amount : 0;
      });
      datasets.push({ label: `${cur} (count)`, data: countData, borderColor: color, backgroundColor: "transparent", fill: false, tension: 0.3, yAxisID: "y" });
      datasets.push({ label: `${cur} (amount)`, data: amountData, borderColor: color, backgroundColor: "transparent", fill: false, tension: 0.3, borderDash: [4, 2], yAxisID: "yAmount" });
    });
    return datasets;
  })();
  const monthlyPaymentChartData = {
    labels: monthlyLabels.length ? monthlyLabels : ["—"],
    datasets: monthlyDatasets,
  };

  const last12MonthsByCurrencyTitle =
    monthlyChartCurrencies.length > 0
      ? `Last 12 months by currency (${monthlyChartCurrencies.join(", ")})`
      : "Last 12 months by currency";

  return (
    <>
      {loading && !paymentAnalytics ? (
        <div className="flex justify-center items-center min-h-[220px]">
          <Loading />
        </div>
      ) : (<>
        <div className="mb-6 lg:mb-8">
          <div className="rounded-2xl overflow-hidden bg-white shadow-md border border-gray-200">
            <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 px-4 sm:px-6 pt-5 pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="font-main text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                  Payments (Subscriptions)
                </h2>
                <div className="flex flex-wrap gap-2">
                  {TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`font-main text-sm font-semibold px-4 py-2.5 rounded-xl transition-all duration-200 ${activeTab === tab.id ? "text-white shadow-md" : "text-gray-600 hover:bg-gray-100 bg-white border border-gray-200"
                        }`}
                      style={activeTab === tab.id ? { backgroundColor: THEME.primary } : {}}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  {activeTab === "overview" ? "Filters (overview & cards)" : "Chart filters – month, year, currency"}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                  {activeTab === "overview" && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Date</label>
                      <input
                        type="date"
                        value={filters.date}
                        onChange={(e) => handleFilterChange("date", e.target.value)}
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Month</label>
                    <select
                      value={filters.month}
                      onChange={(e) => handleFilterChange("month", e.target.value)}
                      className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    >
                      <option value="">All</option>
                      {MONTHS.map((name, i) => (
                        <option key={name} value={i + 1}>{name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Year</label>
                    <select
                      value={filters.year}
                      onChange={(e) => handleFilterChange("year", e.target.value)}
                      className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    >
                      <option value="">All</option>
                      {YEAR_OPTIONS.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Currency</label>
                    <select
                      value={filters.currency}
                      onChange={(e) => handleFilterChange("currency", e.target.value)}
                      className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    >
                      <option value="">All</option>
                      {currencyOptions.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {hasActiveFilters && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Active filters:</span>
                    {activeFilterPills.map(({ key, label }) => (
                      <span key={key} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-red-50 text-red-800 border border-red-200">
                        {label}
                        <button type="button" onClick={() => removeFilter(key)} className="p-0.5 rounded-full hover:bg-red-200 text-red-600 focus:outline-none focus:ring-2 focus:ring-red-400" aria-label={`Remove ${label}`}>
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </span>
                    ))}
                    <button type="button" onClick={handleClear} disabled={loading} className="font-main text-sm font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-60">Clear all</button>
                    {loading && <span className="text-sm text-gray-500">Updating…</span>}
                  </div>
                )}
                {error && <p className="mt-2 text-sm text-red-600 font-medium">{error}</p>}
              </div>
            </div>

            <div className="p-4 sm:p-6 bg-white">
              {loading && !paymentAnalytics && activeTab === "overview" ? (
                <div className="flex justify-center items-center min-h-[220px]">
                  <Loading />
                </div>
              ) : activeTab === "overview" && !paymentAnalytics ? (
                <p className="text-gray-500 text-center py-10">No payment data available.</p>
              ) : activeTab === "overview" ? (
                <>
                  <div className="mb-4">
                    <h3 className="font-main text-lg font-bold text-gray-900 mb-1">Subscription overview (amount calculation)</h3>
                    <p className="text-sm text-gray-500">By default all prices are shown. Select date, month, year or currency above to filter records.</p>
                  </div>
                  <div className={loading ? "opacity-60 pointer-events-none" : ""}>
                    <DashboardPaymentCards paymentAnalytics={paymentAnalytics} selectedCurrency={selectedCurrency} />
                  </div>
                  {todayByCurrency.length > 0 && (
                    <div className="mt-6 p-4 rounded-xl bg-gray-50 border border-gray-200">
                      <p className="text-sm font-semibold text-gray-700 mb-3">Today&apos;s payments by currency</p>
                      <div className="flex flex-wrap gap-3">
                        {todayByCurrency.map((c) => (
                          <span key={c.currency} className="inline-flex items-center px-3 py-2 rounded-lg bg-white border border-gray-200 text-sm font-medium text-gray-800">
                            {formatCurrencyLine(c)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="mb-8">
                    <p className="font-main text-lg font-bold text-gray-800 mb-3">
                      Within a month – Day-by-day ({filters.month && filters.year ? `${MONTHS[chartMonth - 1]} ${chartYear}` : "Select month & year"})
                    </p>
                    <p className="text-sm text-gray-500 mb-2">Left axis: payment count. Right axis: amount.</p>
                    {loadingByDay ? (
                      <div className="min-h-[260px] flex items-center justify-center"><Loading /></div>
                    ) : (
                      <div className="min-h-[260px] sm:h-72">
                        <Line data={paymentsByDayChartData} options={LINE_OPTIONS_DUAL_AXIS} />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-main text-lg font-bold text-gray-800 mb-3">By month – Last 12 months</p>
                    <p className="text-sm text-gray-500 mb-2">Left axis: payment count. Right axis: amount.</p>
                    <div className="min-h-[260px] sm:h-72">
                      <Line data={monthlyPaymentChartData} options={LINE_OPTIONS_DUAL_AXIS} />
                    </div>
                  </div>
                  {monthlyPaymentStatsByCurrency.length > 0 && (
                    <div className="mt-8 p-4 rounded-xl bg-gray-50 border border-gray-200">
                      <p className="font-main text-lg font-bold text-gray-800 mb-3">{last12MonthsByCurrencyTitle}</p>
                      <div className="space-y-4">
                        {monthlyPaymentStatsByCurrency.map((row) => {
                          const byCurrency = filterCurrencies(row.by_currency || []);
                          if (byCurrency.length === 0) return null;
                          return (
                            <div key={`${row.year}-${row.month}`} className="flex flex-wrap items-center gap-3">
                              <span className="text-sm font-semibold text-gray-700 min-w-[100px]">
                                {MONTH_NAMES_SHORT[Number(row.month) - 1]} {row.year}
                              </span>
                              <div className="flex flex-wrap gap-2">
                                {byCurrency.map((c) => (
                                  <span
                                    key={c.currency}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-sm font-medium text-gray-800"
                                  >
                                    {formatCurrencyLine(c)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </>)}
    </>
  );
}
